/**
 * useMonitorWalletsSync Hook
 *
 * Global hook for syncing monitor wallets from Supabase to localStorage
 * after user login. This ensures widgets on HomePage and PortfolioDashboard
 * display data immediately without requiring the user to visit each monitor page.
 *
 * Syncs:
 * - FluxNode wallets → localStorage 'fluxnode_wallets'
 * - Cronos wallets → localStorage 'cronos_wallets'
 * - Hedera accounts → localStorage 'hedera_accounts'
 *
 * Dispatches 'monitor-wallets-synced' event when sync completes so widgets can refresh.
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

// Custom event name for sync completion
export const MONITOR_SYNC_EVENT = 'monitor-wallets-synced';

// Storage keys for each monitor type
export const STORAGE_KEYS = {
  flux: {
    wallets: 'fluxnode_wallets',
    purchaseTracking: 'fluxnode_purchase_tracking',
    appsOwner: 'fluxnode_apps_owner',
  },
  cronos: {
    wallets: 'cronos_wallets',
  },
  hedera: {
    wallets: 'hedera_accounts',
  },
} as const;

type MonitorType = 'flux' | 'cronos' | 'hedera';

interface WalletData {
  wallet_address: string;
  label?: string;
  display_order?: number;
  metadata?: Record<string, unknown>;
}

interface SyncResult {
  flux: string[];
  cronos: string[];
  hedera: string[];
}

interface UseMonitorWalletsSyncReturn {
  syncAll: () => Promise<SyncResult | null>;
  isSyncing: boolean;
  lastSynced: Date | null;
  syncError: Error | null;
  hasWallets: {
    flux: boolean;
    cronos: boolean;
    hedera: boolean;
  };
}

/**
 * Hook for global monitor wallets sync on login
 */
export function useMonitorWalletsSync(): UseMonitorWalletsSyncReturn {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<Error | null>(null);
  const [hasWallets, setHasWallets] = useState({
    flux: false,
    cronos: false,
    hedera: false,
  });
  const hasSyncedRef = useRef(false);

  /**
   * Fetch wallets for a specific monitor type from Supabase
   */
  const fetchWalletsFromCloud = useCallback(async (
    monitorType: MonitorType,
    accessToken: string
  ): Promise<{ wallets: string[]; extras?: Record<string, unknown> } | null> => {
    try {
      const response = await fetch('/api/monitor/wallets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: 'get',
          monitor_type: monitorType,
        }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      const wallets = data.wallets?.map((w: WalletData) => w.wallet_address) || [];

      const result: { wallets: string[]; extras?: Record<string, unknown> } = { wallets };

      // Parse FluxNode extras
      if (monitorType === 'flux') {
        result.extras = {};
        if (data.purchase_tracking) {
          const tracking: Record<string, { purchasePrice: number; purchaseDate?: string }> = {};
          for (const t of data.purchase_tracking) {
            tracking[t.node_id] = {
              purchasePrice: parseFloat(t.purchase_price),
              purchaseDate: t.purchase_date,
            };
          }
          result.extras.purchaseTracking = tracking;
        }
        if (data.apps_owner) {
          result.extras.appsOwner = data.apps_owner;
        }
      }

      return result;
    } catch (e) {
      console.error(`[MonitorSync] Failed to fetch ${monitorType} wallets:`, e);
      return null;
    }
  }, []);

  /**
   * Save wallets to localStorage
   */
  const saveToLocalStorage = useCallback((
    monitorType: MonitorType,
    wallets: string[],
    extras?: Record<string, unknown>
  ) => {
    try {
      const keys = STORAGE_KEYS[monitorType];

      // Save wallets
      if (wallets.length > 0) {
        localStorage.setItem(keys.wallets, JSON.stringify(wallets));
      }

      // Save FluxNode extras
      if (monitorType === 'flux' && extras) {
        if (extras.purchaseTracking) {
          localStorage.setItem(
            STORAGE_KEYS.flux.purchaseTracking,
            JSON.stringify(extras.purchaseTracking)
          );
        }
        if (extras.appsOwner) {
          localStorage.setItem(STORAGE_KEYS.flux.appsOwner, extras.appsOwner as string);
        }
      }

      console.log(`[MonitorSync] Saved ${wallets.length} ${monitorType} wallets to localStorage`);
    } catch (e) {
      console.error(`[MonitorSync] Failed to save ${monitorType} wallets:`, e);
    }
  }, []);

  /**
   * Sync all monitor types from Supabase to localStorage
   */
  const syncAll = useCallback(async (): Promise<SyncResult | null> => {
    // Get access token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.log('[MonitorSync] No session, skipping sync');
      return null;
    }

    setIsSyncing(true);
    setSyncError(null);

    const result: SyncResult = {
      flux: [],
      cronos: [],
      hedera: [],
    };

    try {
      console.log('[MonitorSync] Starting global wallet sync...');

      // Fetch all monitor types in parallel
      const [fluxData, cronosData, hederaData] = await Promise.all([
        fetchWalletsFromCloud('flux', session.access_token),
        fetchWalletsFromCloud('cronos', session.access_token),
        fetchWalletsFromCloud('hedera', session.access_token),
      ]);

      // Process FluxNode wallets
      if (fluxData && fluxData.wallets.length > 0) {
        saveToLocalStorage('flux', fluxData.wallets, fluxData.extras);
        result.flux = fluxData.wallets;
      }

      // Process Cronos wallets
      if (cronosData && cronosData.wallets.length > 0) {
        saveToLocalStorage('cronos', cronosData.wallets);
        result.cronos = cronosData.wallets;
      }

      // Process Hedera accounts
      if (hederaData && hederaData.wallets.length > 0) {
        saveToLocalStorage('hedera', hederaData.wallets);
        result.hedera = hederaData.wallets;
      }

      // Update hasWallets state
      setHasWallets({
        flux: result.flux.length > 0,
        cronos: result.cronos.length > 0,
        hedera: result.hedera.length > 0,
      });

      // Invalidate React Query caches to trigger refetch in widgets
      if (result.flux.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['fluxNodes'] });
        queryClient.invalidateQueries({ queryKey: ['fluxPrice'] });
      }
      if (result.cronos.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['cronosWallets'] });
        queryClient.invalidateQueries({ queryKey: ['croPrice'] });
      }
      if (result.hedera.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['hederaWallets'] });
        queryClient.invalidateQueries({ queryKey: ['hbarPrice'] });
      }

      setLastSynced(new Date());
      console.log('[MonitorSync] Sync complete:', {
        flux: result.flux.length,
        cronos: result.cronos.length,
        hedera: result.hedera.length,
      });

      // Dispatch custom event so widgets can refresh their data
      window.dispatchEvent(new CustomEvent(MONITOR_SYNC_EVENT, {
        detail: result,
      }));

      return result;
    } catch (e) {
      console.error('[MonitorSync] Sync failed:', e);
      setSyncError(e instanceof Error ? e : new Error('Sync failed'));
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [fetchWalletsFromCloud, saveToLocalStorage, queryClient]);

  /**
   * Auto-sync on login
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset state on logout
      hasSyncedRef.current = false;
      setHasWallets({ flux: false, cronos: false, hedera: false });
      return;
    }

    // Only sync once per session
    if (hasSyncedRef.current) return;

    // Delay sync slightly to ensure auth is fully established
    const syncTimeout = setTimeout(async () => {
      hasSyncedRef.current = true;
      await syncAll();
    }, 500);

    return () => clearTimeout(syncTimeout);
  }, [isAuthenticated, syncAll]);

  return {
    syncAll,
    isSyncing,
    lastSynced,
    syncError,
    hasWallets,
  };
}

/**
 * Check if localStorage has wallets for monitors (without sync)
 */
export function checkLocalStorageWallets(): { flux: string[]; cronos: string[]; hedera: string[] } {
  const result = {
    flux: [] as string[],
    cronos: [] as string[],
    hedera: [] as string[],
  };

  try {
    const fluxRaw = localStorage.getItem(STORAGE_KEYS.flux.wallets);
    if (fluxRaw) {
      const parsed = JSON.parse(fluxRaw);
      if (Array.isArray(parsed)) result.flux = parsed;
    }

    const cronosRaw = localStorage.getItem(STORAGE_KEYS.cronos.wallets);
    if (cronosRaw) {
      const parsed = JSON.parse(cronosRaw);
      if (Array.isArray(parsed)) result.cronos = parsed;
    }

    const hederaRaw = localStorage.getItem(STORAGE_KEYS.hedera.wallets);
    if (hederaRaw) {
      const parsed = JSON.parse(hederaRaw);
      if (Array.isArray(parsed)) result.hedera = parsed;
    }
  } catch (e) {
    console.error('[MonitorSync] Error reading localStorage:', e);
  }

  return result;
}
